import { adsApi } from '@/api/client';
import { formatBidUpdateSuccessMessage } from '@/api/keywordBidUpdate';
import type { SpTargetRow } from '@/api/spTargets';

export type TargetAdProduct = 'SP' | 'SB';

export type EditableTargetBidTarget = {
  adProduct: TargetAdProduct;
  targetId: string;
  bid: number;
  campaignId?: string;
  adGroupId?: string;
  state?: string;
};

type TargetMutationResult = {
  targetId?: string | null;
  status?: string;
  message?: string;
};

function coerceDisplayString(value: unknown): string {
  if (value == null) return '';
  const text = String(value).trim();
  return text;
}

function parseMutationResults(data: unknown): TargetMutationResult[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const results = raw.results;
  if (!Array.isArray(results)) return [];
  return results as TargetMutationResult[];
}

function assertMutationSuccess(
  results: TargetMutationResult[],
  targetId: string
): void {
  const match =
    results.find((item) => item.targetId && String(item.targetId) === targetId) ??
    results[0];
  if (!match) return;
  if (match.status === 'error') {
    throw new Error(match.message || 'Amazon rejected the bid update');
  }
}

export function resolveEditableSpTargetBid(target: SpTargetRow): EditableTargetBidTarget | null {
  const targetId = coerceDisplayString(target.target_id);
  if (!targetId) return null;
  if (typeof target.bid !== 'number' || !Number.isFinite(target.bid)) return null;

  return {
    adProduct: 'SP',
    targetId,
    bid: target.bid,
  };
}

export function resolveEditableSbTargetBid(target: SpTargetRow): EditableTargetBidTarget | null {
  const targetId = coerceDisplayString(target.target_id);
  if (!targetId) return null;
  if (typeof target.bid !== 'number' || !Number.isFinite(target.bid)) return null;

  const campaignId = coerceDisplayString(target.campaign_id);
  const adGroupId = coerceDisplayString(target.ad_group_id);
  if (!campaignId || !adGroupId) return null;

  const state = coerceDisplayString(target.state).toLowerCase() || 'enabled';
  return {
    adProduct: 'SB',
    targetId,
    bid: target.bid,
    campaignId,
    adGroupId,
    state,
  };
}

export async function applyTargetBidUpdate(
  profileId: string,
  target: EditableTargetBidTarget,
  newBid: number
): Promise<void> {
  if (!Number.isFinite(newBid) || newBid <= 0) {
    throw new Error('Enter a valid bid greater than 0');
  }

  if (target.adProduct === 'SP') {
    const res = await adsApi.updateSpTargetBid(profileId, {
      targetId: target.targetId,
      bid: newBid,
    });
    if (!res.success) throw new Error('Failed to update bid');
    assertMutationSuccess(parseMutationResults(res.data), target.targetId);
    return;
  }

  if (!target.campaignId || !target.adGroupId) {
    throw new Error('Missing campaign or ad group for SB target');
  }

  const res = await adsApi.updateSbTargetBid(profileId, {
    targetId: target.targetId,
    campaignId: target.campaignId,
    adGroupId: target.adGroupId,
    bid: newBid,
    state: target.state || 'enabled',
  });
  if (!res.success) throw new Error('Failed to update bid');
  assertMutationSuccess(parseMutationResults(res.data), target.targetId);
}

export { formatBidUpdateSuccessMessage };
